import { ApiService } from '../ApiService';
import { Post, PostsView } from '../../models/Post';
import { CommentsView } from '../../models/Comment';
import { SearchedTerms } from '../../models/SearchedTerms';
import { plainToClass } from '~/models/transform';
import { MembersView } from '~/models/Member';
import { BaseListRequestParams } from '~/models/BaseRequestParams';

/**
 * Represents the muted state of a post
 * - true: The post is muted by the user
 * - false: The post is not muted by the user
 * - null: Mute state not applicable for this post type
 */
export interface IPostMutedState {
  mutedState: boolean | null;
}

export class PostsService {
  static async getNotifications(params?: BaseListRequestParams): Promise<PostsView> {
    const response = await ApiService.get<PostsView>('posts/notifications', params);
    return plainToClass(PostsView, response);
  }

  static async dismissAllNotifications(): Promise<void> {
    await ApiService.delete('posts/notifications');
  }

  static async dismissNotification(notificationId: number): Promise<void> {
    await ApiService.delete(`posts/notifications/${notificationId}`);
  }

  static async getPost(id: number | string): Promise<Post> {
    const response = await ApiService.get<Post>(`posts/${id}`);
    return plainToClass(Post, response);
  }
  
  static async getPosts(
    feed: 'global' | 'local' | 'network' | 'search' = 'global',
    params?: BaseListRequestParams & { radiusKm?: number; query?: string }
  ): Promise<PostsView> {
    const response = await ApiService.get<PostsView>(`posts/feed/${feed}`, params);
    return plainToClass(PostsView, response);
  }

  static async likePost(post: Post): Promise<any> {
    const endpoint = post.PhotoId ? `photos/${post.PhotoId}/like` : `posts/${post.Id}/like`;

    return ApiService.post(endpoint, {});
  }

  static async unlikePost(post: Post): Promise<any> {
    const endpoint = post.PhotoId ? `photos/${post.PhotoId}/unlike` : `posts/${post.Id}/unlike`;

    return ApiService.post(endpoint, {});
  }

  static async getSearchedTerms(options?: {
    popularOnly?: boolean;
  }): Promise<SearchedTerms> {
    const params = options?.popularOnly ? { popularOnly: true } : undefined;
    const response = await ApiService.get<SearchedTerms>(
      'posts/searched-terms',
      params
    );
    return response;
  }

  static async getComments(post: Post, params?: BaseListRequestParams): Promise<CommentsView> {
    const endpoint = post.PhotoId
      ? `photos/${post.PhotoId}/comments`
      : `posts/${post.Id}/comments`;

    const response = await ApiService.get<CommentsView>(endpoint, params);
    return plainToClass(CommentsView, response);
  }

  static async getLikes(post: Post, params?: BaseListRequestParams): Promise<MembersView> {
    const endpoint = post.PhotoId
      ? `photos/${post.PhotoId}/likes`
      : `posts/${post.Id}/likes`;

    const response = await ApiService.get<MembersView>(endpoint, params);
    return plainToClass(MembersView, response);
  }

  static async mutePost(postId: number, minutes: number = -1): Promise<void> {
    const endpoint = `posts/${postId}/mute?minutes=${minutes}`;
    await ApiService.post(endpoint, {});
  }

  static async unmutePost(postId: number): Promise<void> {
    const endpoint = `posts/${postId}/unmute`;
    await ApiService.post(endpoint, {});
  }

  static async getMutedState(postId: number): Promise<IPostMutedState> {
    const endpoint = `posts/${postId}/muted-state`;
    return await ApiService.get<IPostMutedState>(endpoint);
  }

  static async hideSticky(postId: number): Promise<void> {
    const endpoint = `posts/${postId}/hide-sticky`;
    await ApiService.post(endpoint, {});
  }

  static async deletePost(postId: number): Promise<void> {
    const endpoint = `posts/${postId}`;
    await ApiService.delete(endpoint);
  }

  static async addComment(post: Post, text: string): Promise<void> {
    const endpoint = post.PhotoId ? `photos/${post.PhotoId}/comment` : `posts/${post.Id}/comment`;
    await ApiService.post(endpoint, { Text: text });
  }

  static async createPost(text: string): Promise<void> {
    const endpoint = 'posts/new';
    await ApiService.post(endpoint, { Text: text });
  }
  
  static async editPost(postId: number, text: string): Promise<Post> {
    const endpoint = `posts/${postId}`;
    const response = await ApiService.patch<Post>(endpoint, { Text: text });
    return plainToClass(Post, response);
  }

  /**
   * Accepts a photo unlock request associated with a post.
   * @param postId The ID of the post for which to accept the photo unlock
   */
  static async acceptPhotoUnlock(postId: number): Promise<void> {
    const endpoint = `posts/${postId}/photo-unlock/accept`;
    await ApiService.post(endpoint, {});
  }

  /**
   * Rejects a photo unlock request associated with a post.
   * @param postId The ID of the post for which to reject the photo unlock
   */
  static async rejectPhotoUnlock(postId: number): Promise<void> {
    const endpoint = `posts/${postId}/photo-unlock/reject`;
    await ApiService.post(endpoint, {});
  }
}
